// ShiftFunctionByVectorTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import EquationInput from "../../../../components/inputs/EquationInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

/* =========================
   Helpers
========================= */

const randInt = (min, max, nonZero = false) => {
  let x;
  do {
    x = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (nonZero && x === 0);
  return x;
};

// usuwa "g(x)=", "f(x)=", "y=" tylko na początku
const stripLeadingAssignment = (s) => {
  let t = String(s ?? "");
  t = t.replace(/−/g, "-").trim();
  t = t.replace(/^(g|f)\(x\)\s*=\s*/i, "");
  t = t.replace(/^y\s*=\s*/i, "");
  return t.trim();
};

const normalizeExpr = (v) => {
  const raw = typeof v === "object" ? v?.raw ?? "" : v ?? "";
  let s = String(raw);

  s = s.replace(/−/g, "-");
  s = s.replace(/\s+/g, "");
  s = s.replace(/^(g|f)\(x\)=/i, "");
  s = s.replace(/^y=/i, "");
  s = s.replace(/\*/g, "");

  for (let i = 0; i < 6; i++) {
    s = s.replace(/\+\+/g, "+");
    s = s.replace(/--/g, "+");
    s = s.replace(/\+-/g, "-");
    s = s.replace(/-\+/g, "-");
  }

  // nawias tylko wokół liczby: (+3)->3, (-3)->-3
  s = s.replace(/\(\+?(-?\d+)\)/g, "$1");
  return s;
};

// do latex: "+ 3" / "- 3" / "" dla 0
const latexAdd = (n) => {
  if (n === 0) return "";
  return n < 0 ? `- ${Math.abs(n)}` : `+ ${n}`;
};

// do latex w środku "x - p": jeżeli p>=0 to "x-3", jeżeli p<0 to "x-(-3)"
const latexXMinusP = (p) => (p < 0 ? `x-(${p})` : `x-${p}`);

// ładniejszy zapis do kolejnych kroków (bez "minus minus")
const latexInsideNice = (p) => {
  if (p === 0) return "x";
  return p < 0 ? `x+${Math.abs(p)}` : `x-${p}`;
};

function latexAx(a) {
  if (a === 1) return "x";
  if (a === -1) return "-x";
  return `${a}x`;
}

function latexCx(c) {
  if (c === 0) return "";
  return c < 0 ? `- ${Math.abs(c)}` : `+ ${c}`;
}

/** JS expr for GraphViewer equations */
function exprAx(a) {
  if (a === 1) return "x";
  if (a === -1) return "-x";
  return `${a} * x`;
}

function exprAxx(A) {
  if (A === 1) return "x * x";
  if (A === -1) return "-x * x";
  return `${A} * x * x`;
}

function exprAxxx(A) {
  if (A === 1) return "x * x * x";
  if (A === -1) return "-x * x * x";
  return `${A} * x * x * x`;
}

const prettyVectorSquareLatex = (p, q) => {
  // bez nawiasów na ujemnych ([-1, 2], a nie [(-1), 2])
  return `\\left[${p},\\ ${q}\\right]`;
};

// Latex wielomianu z współczynników stopnia 3..0 (zerowe pomija)
function polyLatex({ a3 = 0, a2 = 0, a1 = 0, a0 = 0 }) {
  const parts = [];

  const push = (coef, core) => {
    if (coef === 0) return;
    const sign = coef < 0 ? "-" : "+";
    const abs = Math.abs(coef);
    const coefStr = abs === 1 && core !== "" ? "" : String(abs);
    const term = core === "" ? `${abs}` : `${coefStr}${core}`;
    if (parts.length === 0) {
      parts.push(coef < 0 ? `-${term}` : `${term}`);
    } else {
      parts.push(`${sign} ${term}`);
    }
  };

  push(a3, "x^3");
  push(a2, "x^2");
  push(a1, "x");
  push(a0, "");

  return parts.join(" ").replace(/\s+/g, " ").trim() || "0";
}

/* =========================
   Funkcje bazowe + wynikowe
========================= */

function makeBaseFunction(forceKind) {
  // trochę więcej różnorodności: liniowa / kwadratowa / sześcienna (bez x^2)
  const r = Math.random();
  const kind = forceKind || (r < 0.4 ? "linear" : r < 0.8 ? "quadratic" : "cubic");

  if (kind === "linear") {
    // blokujemy a=1; a=-1 OK
    let a = randInt(-5, 5, true);
    while (a === 1) a = randInt(-5, 5, true);

    const b = randInt(-7, 7, false);
    return { kind: "linear", a, b };
  }

  if (kind === "quadratic") {
    // Ax^2 + Bx + C
    const A = randInt(-3, 3, true);
    const B = randInt(-6, 6, false);
    const C = randInt(-6, 6, false);
    return { kind: "quadratic", A, B, C };
  }

  // cubic: Ax^3 + Bx + C (współczynnik przy x^2 = 0)
  const A = randInt(-2, 2, true);
  const B = randInt(-6, 6, false);
  const C = randInt(-6, 6, false);
  return { kind: "cubic", A, B, C };
}

function baseFunctionLatex(base) {
  if (base.kind === "linear") {
    const { a, b } = base;
    const ax = latexAx(a);
    if (b === 0) return `${ax}`;
    return `${ax} ${b < 0 ? "- " + Math.abs(b) : "+ " + b}`;
  }

  if (base.kind === "quadratic") {
    const { A, B, C } = base;
    const Ax2 = A === 1 ? "x^2" : A === -1 ? "-x^2" : `${A}x^2`;
    const Bx =
      B === 0
        ? ""
        : B === 1
        ? "+ x"
        : B === -1
        ? "- x"
        : B < 0
        ? `- ${Math.abs(B)}x`
        : `+ ${B}x`;
    const Cc = C === 0 ? "" : C < 0 ? `- ${Math.abs(C)}` : `+ ${C}`;
    return `${Ax2} ${Bx} ${Cc}`.replace(/\s+/g, " ").trim();
  }

  // cubic
  const { A, B, C } = base;
  return polyLatex({ a3: A, a2: 0, a1: B, a0: C });
}

function expandedAnswerLatex(base, p, q) {
  // g(x)=f(x-p)+q
  if (base.kind === "linear") {
    const { a, b } = base;
    const c = b - a * p + q;
    const ax = latexAx(a);
    const cc = latexCx(c);
    return cc ? `${ax} ${cc}` : `${ax}`;
  }

  if (base.kind === "quadratic") {
    const { A, B, C } = base;
    const bNew = B - 2 * A * p;
    const cNew = A * p * p - B * p + C + q;
    return polyLatex({ a3: 0, a2: A, a1: bNew, a0: cNew });
  }

  // cubic: f(x)=Ax^3 + Bx + C
  // g(x)=A(x-p)^3 + B(x-p) + C + q
  // (x-p)^3 = x^3 - 3px^2 + 3p^2 x - p^3
  const { A, B, C } = base;
  const a3 = A;
  const a2 = -3 * A * p;
  const a1 = 3 * A * p * p + B;
  const a0 = -A * p * p * p - B * p + C + q;
  return polyLatex({ a3, a2, a1, a0 });
}

function acceptedNormalizedForms(base, p, q) {
  const forms = [];

  if (base.kind === "linear") {
    const { a, b } = base;

    // definicyjna: a(x-p) + (b+q)
    const inside = latexXMinusP(p);
    const c1 = b + q;
    const f1 = c1 === 0 ? `${a}(${inside})` : `${a}(${inside})${c1 >= 0 ? "+" : ""}${c1}`;
    forms.push(normalizeExpr(f1));

    // uproszczona: ax + (b - ap + q)
    const c2 = b - a * p + q;
    const f2 = c2 === 0 ? `${a}x` : `${a}x${c2 >= 0 ? "+" : ""}${c2}`;
    forms.push(normalizeExpr(f2));

    if (a === 1) forms.push(normalizeExpr(c2 === 0 ? `x` : `x${c2 >= 0 ? "+" : ""}${c2}`));
    if (a === -1) forms.push(normalizeExpr(c2 === 0 ? `-x` : `-x${c2 >= 0 ? "+" : ""}${c2}`));
  } else if (base.kind === "quadratic") {
    const { A, B, C } = base;

    const inside = latexXMinusP(p);
    const f1 = `${A}(${inside})^2+${B}(${inside})+${C}+${q}`;
    forms.push(normalizeExpr(f1));

    const bNew = B - 2 * A * p;
    const cNew = A * p * p - B * p + C + q;
    const f2 = `${A}x^2${bNew >= 0 ? "+" : ""}${bNew}x${cNew >= 0 ? "+" : ""}${cNew}`;
    forms.push(normalizeExpr(f2));

    if (A === 1) forms.push(normalizeExpr(`x^2${bNew >= 0 ? "+" : ""}${bNew}x${cNew >= 0 ? "+" : ""}${cNew}`));
    if (A === -1) forms.push(normalizeExpr(`-x^2${bNew >= 0 ? "+" : ""}${bNew}x${cNew >= 0 ? "+" : ""}${cNew}`));
  } else {
    // cubic
    const { A, B, C } = base;
    const inside = latexXMinusP(p);

    // definicyjna: A(x-p)^3 + B(x-p) + C + q
    const f1 = `${A}(${inside})^3+${B}(${inside})+${C}+${q}`;
    forms.push(normalizeExpr(f1));

    // rozwinięta
    const a2 = -3 * A * p;
    const a1 = 3 * A * p * p + B;
    const a0 = -A * p * p * p - B * p + C + q;
    const f2 = `${A}x^3${a2 >= 0 ? "+" : ""}${a2}x^2${a1 >= 0 ? "+" : ""}${a1}x${a0 >= 0 ? "+" : ""}${a0}`;
    forms.push(normalizeExpr(f2));

    if (A === 1) forms.push(normalizeExpr(`x^3${a2 >= 0 ? "+" : ""}${a2}x^2${a1 >= 0 ? "+" : ""}${a1}x${a0 >= 0 ? "+" : ""}${a0}`));
    if (A === -1) forms.push(normalizeExpr(`-x^3${a2 >= 0 ? "+" : ""}${a2}x^2${a1 >= 0 ? "+" : ""}${a1}x${a0 >= 0 ? "+" : ""}${a0}`));
  }

  return Array.from(new Set(forms));
}

const variantPick = () => {
  const r = Math.random();
  if (r < 1 / 3) return "x";
  if (r < 2 / 3) return "y";
  return "xy";
};

/* =========================
   Generator
========================= */

export function generateTask(forceVariant, options = {}) {
  if (forceVariant && typeof forceVariant === "object") {
    options = forceVariant;
    forceVariant = options.variant;
  }
  const variant = forceVariant || variantPick();
  const base = makeBaseFunction(options.kind);

  let p = 0;
  let q = 0;

  if (variant === "x") {
    p = randInt(-4, 4, true);
    q = 0;
  } else if (variant === "y") {
    p = 0;
    q = randInt(-5, 5, true);
  } else {
    p = randInt(-4, 4, true);
    q = randInt(-5, 5, true);
  }

  const answerLatex = expandedAnswerLatex(base, p, q);
  const accepted = acceptedNormalizedForms(base, p, q);

  return {
    type: "ShiftFunctionByVectorTask",
    data: {
      variant,
      base,
      p,
      q,
      answerLatex,
      accepted,
    },
  };
}

/* =========================
   Render task
========================= */

export function renderTask({ data }) {
  const { base, p, q, variant } = data;

  let shiftLine = null;

  if (variant === "x") {
    const m = Math.abs(p);
    shiftLine = (
      <div style={{ marginTop: 6 }}>
        Wykres funkcji <InlineMath math={"f"} /> przesunięto <InlineMath math={String(m)} />{" "}
        {p < 0 ? "w lewo" : "w prawo"}.
      </div>
    );
  } else if (variant === "y") {
    const m = Math.abs(q);
    shiftLine = (
      <div style={{ marginTop: 6 }}>
        Wykres funkcji <InlineMath math={"f"} /> przesunięto <InlineMath math={String(m)} />{" "}
        {q < 0 ? "w dół" : "w górę"}.
      </div>
    );
  } else {
    shiftLine = (
      <div style={{ marginTop: 6 }}>
        Wykres funkcji <InlineMath math={"f"} /> przesunięto o wektor{" "}
        <InlineMath math={prettyVectorSquareLatex(p, q)} />.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div>
        Dana jest funkcja:
        <BlockMath math={`f(x) = ${baseFunctionLatex(base)}`} />
      </div>

      {shiftLine}

      <div style={{ marginTop: 10 }}>
        Podaj wzór funkcji <InlineMath math={"g"} /> w postaci <strong>uporządkowanego wielomianu</strong>
        {" "} (po wymnożeniu i redukcji).
      </div>
    </div>
  );
}


/* =========================
   Input + podgląd
========================= */

export function renderInput(_task, userInput = {}, onChange, disabled) {
  const ui = userInput && typeof userInput === "object" ? userInput : {};
  const value = ui.eq ?? "";

  const handleChange = (v) => {
    const base = userInput && typeof userInput === "object" ? userInput : {};
    onChange({ ...base, eq: v });
  };

  const raw = typeof value === "object" ? value?.raw ?? "" : String(value ?? "");
  const cleaned = stripLeadingAssignment(raw);
  const has = cleaned.trim().length > 0;

  return (
    <div className="flex flex-col gap-2" style={{ marginTop: 8 }}>
      <div className="flex items-center gap-2">
        <InlineMath math={"g(x)="} />
        <div style={{ minWidth: 260 }}>
          <EquationInput
            value={value}
            onChange={handleChange}
            disabled={disabled}
            showPreview={false}
            placeholder={"np. 2x^3-4x^2+3x-1"}
          />
        </div>
      </div>

      {has ? (
        <div style={{ marginTop: 2 }}>
          <strong>Podgląd:</strong> <InlineMath math={`g(x)=${cleaned}`} />
        </div>
      ) : null}
    </div>
  );
}


/* =========================
   Check
========================= */

export function checkAnswer(task, userInput = {}) {
  const accepted = task?.data?.accepted || [];
  const val = userInput?.eq;

  const got = normalizeExpr(val);
  if (!got) return false;

  return accepted.includes(got);
}

/* =========================
   Answer
========================= */

export function renderAnswer({ data }) {
  return <InlineMath math={`g(x)=${data.answerLatex}`} />;
}

/* =========================
   Explanation (kroki + rachunki)
========================= */

export function renderExplanation({ data }) {
  const { base, p, q, variant, answerLatex } = data;

  // 1) Pierwsze podstawienie pokazujemy "x-(-3)" dla p<0.
  // 2) W kolejnych krokach upraszczamy do "x+3".
  // 3) Dla p=0 pokazujemy po prostu "x" (zamiast x-0).
  const insideRaw = p === 0 ? "x" : latexXMinusP(p);
  const insideNice = latexInsideNice(p);

  // reguła (poprawka: gdy p=0 to nie pokazujemy f(x-0))
  const ruleLatex =
    p === 0
      ? q === 0
        ? `g(x)=f(x)`
        : `g(x)=f(x) ${latexAdd(q)}`
      : q === 0
      ? `g(x)=f\\big(${insideRaw}\\big)`
      : `g(x)=f\\big(${insideRaw}\\big) ${latexAdd(q)}`;

  const ruleText =
    variant === "x"
      ? "Przesunięcie w lewo/prawo: w argumencie funkcji podstawiamy x → x - p (uważaj na znak p)."
      : variant === "y"
      ? "Przesunięcie w górę/dół: do wartości funkcji dodajemy q."
      : "Dla wektora [p, q] wykonujemy oba kroki: x → x - p oraz dodajemy q.";

  const formulaLatex =
    p === 0
      ? q === 0
        ? "g(x)=f(x)"
        : "g(x)=f(x)+q"
      : q === 0
      ? "g(x)=f(x-p)"
      : "g(x)=f(x-p)+q";

  let stepsLatex = [];

  if (base.kind === "linear") {
    const { a, b } = base;
    const c = b - a * p + q;

    const aStr = a === 1 ? "" : a === -1 ? "-" : `${a}`;

    const L1 = ruleLatex;
    const L2 =
      p === 0
        ? `= ${aStr}x ${latexAdd(b)} ${latexAdd(q)}`
        : `= ${aStr}\\big(${insideNice}\\big) ${latexAdd(b)} ${latexAdd(q)}`;
    const L3 = `= ${latexAx(a)} ${latexCx(c)}`;
    const L4 = `= ${answerLatex}`;

    stepsLatex = [L1, L2, L3, L4].map((x) => x.replace(/\s+/g, " ").trim());
  } else if (base.kind === "quadratic") {
    const { A, B, C } = base;
    const bNew = B - 2 * A * p;
    const cNew = A * p * p - B * p + C + q;

    const L1 = ruleLatex;
    const L2 =
      p === 0
        ? `= ${A}x^2 + ${B}x + ${C} ${latexAdd(q)}`
        : `= ${A}\\big(${insideNice}\\big)^2 + ${B}\\big(${insideNice}\\big) + ${C} ${latexAdd(q)}`;
    const L3 =
      p === 0
        ? `= ${A}x^2 + ${B}x + ${C} ${latexAdd(q)}`
        : `= ${A}\\big(x^2 - 2\\cdot(${p})x + (${p})^2\\big) + ${B}x - ${B}\\cdot(${p}) + ${C} ${latexAdd(q)}`;
    const L4 = `= ${A}x^2 + (${bNew})x + (${cNew})`;
    const L5 = `= ${answerLatex}`;

    stepsLatex = [L1, L2, L3, L4, L5].map((x) => x.replace(/\s+/g, " ").trim());
  } else {
    // cubic
    const { A, B, C } = base;

    const a2 = -3 * A * p;
    const a1 = 3 * A * p * p + B;
    const a0 = -A * p * p * p - B * p + C + q;

    const L1 = ruleLatex;
    const L2 =
      p === 0
        ? `= ${A}x^3 + ${B}x + ${C} ${latexAdd(q)}`
        : `= ${A}\\big(${insideNice}\\big)^3 + ${B}\\big(${insideNice}\\big) + ${C} ${latexAdd(q)}`;

    const L3 =
      p === 0
        ? `= ${A}x^3 + ${B}x + ${C} ${latexAdd(q)}`
        : `= ${A}\\big(x^3 - 3\\cdot(${p})x^2 + 3\\cdot(${p})^2x - (${p})^3\\big) + ${B}x - ${B}\\cdot(${p}) + ${C} ${latexAdd(q)}`;

    const L4 = `= ${A}x^3 + (${a2})x^2 + (${a1})x + (${a0})`;
    const L5 = `= ${answerLatex}`;

    stepsLatex = [L1, L2, L3, L4, L5].map((x) => x.replace(/\s+/g, " ").trim());
  }

  // wykresy: f i g
  let eqF = "";
  let eqG = "";

  if (base.kind === "linear") {
    const { a, b } = base;
    const c = b - a * p + q;

    const bPart = b === 0 ? "" : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
    const cPart = c === 0 ? "" : c > 0 ? ` + ${c}` : ` - ${Math.abs(c)}`;
    eqF = `y = ${exprAx(a)}${bPart}`;
    eqG = `y = ${exprAx(a)}${cPart}`;
  } else if (base.kind === "quadratic") {
    const { A, B, C } = base;
    const bNew = B - 2 * A * p;
    const cNew = A * p * p - B * p + C + q;

    const Bpart = B === 0 ? "" : B > 0 ? ` + ${B} * x` : ` - ${Math.abs(B)} * x`;
    const Cpart = C === 0 ? "" : C > 0 ? ` + ${C}` : ` - ${Math.abs(C)}`;
    eqF = `y = ${exprAxx(A)}${Bpart}${Cpart}`;

    const bNpart = bNew === 0 ? "" : bNew > 0 ? ` + ${bNew} * x` : ` - ${Math.abs(bNew)} * x`;
    const cNpart = cNew === 0 ? "" : cNew > 0 ? ` + ${cNew}` : ` - ${Math.abs(cNew)}`;
    eqG = `y = ${exprAxx(A)}${bNpart}${cNpart}`;
  } else {
    const { A, B, C } = base;
    const a2 = -3 * A * p;
    const a1 = 3 * A * p * p + B;
    const a0 = -A * p * p * p - B * p + C + q;

    const Bpart = B === 0 ? "" : B > 0 ? ` + ${B} * x` : ` - ${Math.abs(B)} * x`;
    const Cpart = C === 0 ? "" : C > 0 ? ` + ${C}` : ` - ${Math.abs(C)}`;
    eqF = `y = ${exprAxxx(A)}${Bpart}${Cpart}`;

    const a2part = a2 === 0 ? "" : a2 > 0 ? ` + ${a2} * x * x` : ` - ${Math.abs(a2)} * x * x`;
    const a1part = a1 === 0 ? "" : a1 > 0 ? ` + ${a1} * x` : ` - ${Math.abs(a1)} * x`;
    const a0part = a0 === 0 ? "" : a0 > 0 ? ` + ${a0}` : ` - ${Math.abs(a0)}`;
    eqG = `y = ${exprAxxx(A)}${a2part}${a1part}${a0part}`;
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Zapisujemy regułę przesunięcia.
      </div>
      <div>{ruleText}</div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Korzystamy ze wzoru <InlineMath math={formulaLatex} /> i podstawiamy{" "}
        {p === 0 ? null : <InlineMath math={`p=${p}`} />}
        {p === 0 || q === 0 ? null : ", "}
        {q === 0 ? null : <InlineMath math={`q=${q}`} />}.
      </div>

      <BlockMath math={ruleLatex} />

      <div style={{ marginTop: 8 }}>
        <strong>Krok 3.</strong> Podstawiamy i przekształcamy:
      </div>

      <div style={{ marginTop: 6 }}>
        {stepsLatex.map((L, idx) => (
          <BlockMath key={idx} math={L} />
        ))}
      </div>

      <div style={{ marginTop: 12 }}>
        <strong>Wizualizacja (przed i po przesunięciu):</strong>
        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flexWrap: "wrap" }}>
          <GraphViewer
            equations={[
              { expr: eqF, color: "purple", label: "f" },
              { expr: eqG, color: "orange", label: "g" },
            ]}
            xRange={[-8, 8]}
            yRange={[-8, 8]}
            showGrid
            width={420}
            height={420}
          />

          <div style={{ fontSize: "1rem", lineHeight: 1.6 }}>
            <div style={{ color: "purple" }}>
              <InlineMath math={`f(x)=${baseFunctionLatex(base)}`} />
            </div>
            <div style={{ color: "orange" }}>
              <InlineMath math={`g(x)=${answerLatex}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const val = userInput?.eq;
  const raw = typeof val === "object" ? val?.raw ?? "" : String(val ?? "");
  const cleaned = stripLeadingAssignment(raw);

  if (!cleaned.trim()) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`g(x)=${cleaned.trim()}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
